// Pure helpers for the search page's structured geo filter — the single `geo=` URL param
// and the `geo` field on the search request. The filter object is the compact shape both
// sides share: { label, placeId, lat, lng, city, region, regionCode, countryCode,
// granularity, radiusKm }.
//
// The URL param carries the FULL object (JSON-encoded), not just a place id: Mapbox ids
// are only stable within a billing session, so re-retrieving on back-navigation would be
// both costly and unreliable — full-object serialization restores free and deterministic.

// Granularities the filter can act on (camelCase — the wire form of GeoGranularity).
// "unknown" is rejected: a filter that can't say what its coordinate pins matches nothing sensibly.
const KNOWN_GRANULARITIES = ["country", "region", "place", "address"];

const STRING_FIELDS = ["label", "placeId", "city", "region", "regionCode", "countryCode"];

// Validates + strips an arbitrary candidate object down to the compact filter shape, in a
// fixed field order (the serialized string doubles as an identity key for the search page's
// selection scoping). Returns null unless lat/lng are finite and granularity is known —
// never a half-filter.
function normalize(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;
  if (!Number.isFinite(obj.lat) || !Number.isFinite(obj.lng)) return null;
  // Off-globe coordinates (a mangled shareable link) degrade to "no filter" here instead of
  // travelling to the backend as a doomed request.
  if (Math.abs(obj.lat) > 90 || Math.abs(obj.lng) > 180) return null;
  if (!KNOWN_GRANULARITIES.includes(obj.granularity)) return null;
  const out = {};
  for (const f of STRING_FIELDS) {
    if (typeof obj[f] === "string" && obj[f]) out[f] = obj[f];
  }
  out.lat = obj.lat;
  out.lng = obj.lng;
  out.granularity = obj.granularity;
  out.radiusKm = Number.isFinite(obj.radiusKm) && obj.radiusKm > 0 ? obj.radiusKm : 0;
  return out;
}

// Builds a filter from a retrieved GeoLocation (the /geo/retrieve response shape) — maps
// displayName → label. Null when the geo can't back a filter (e.g. a ToLocality-projected
// geo with no coordinates, or unknown granularity).
export function fromGeoLocation(geo, radiusKm = 0) {
  if (!geo) return null;
  return normalize({
    label: geo.displayName,
    placeId: geo.placeId,
    lat: geo.lat,
    lng: geo.lng,
    city: geo.city,
    region: geo.region,
    regionCode: geo.regionCode,
    countryCode: geo.countryCode,
    granularity: geo.granularity,
    radiusKm,
  });
}

// Serializes for the `geo=` URL param. A zero radius is dropped so "no radius" and an
// explicit 0 produce byte-identical params.
export function serializeGeoFilter(filter) {
  const norm = normalize(filter);
  if (!norm) return "";
  if (!(norm.radiusKm > 0)) delete norm.radiusKm;
  return JSON.stringify(norm);
}

// Parses the `geo=` URL param back into a filter. Garbage in any form — malformed JSON,
// non-object JSON, wrong field types, non-finite coords, unknown granularity — yields null.
export function parseGeoFilter(raw) {
  if (!raw || typeof raw !== "string") return null;
  try {
    return normalize(JSON.parse(raw));
  } catch {
    return null;
  }
}

// Radius only makes sense around a point-like pick — a city or address. Around a region or
// country centroid a 25 km circle is meaningless (and the backend refuses to apply one).
export function radiusEligible(filter) {
  return !!filter && (filter.granularity === "place" || filter.granularity === "address");
}

// The `geo` field for the search request (the API contract shape). radiusKm is omitted
// unless the filter is radius-eligible AND positive — a radius on a region/country pick
// must never reach the backend.
export function geoRequestField(filter) {
  const norm = normalize(filter);
  if (!norm) return null;
  const { radiusKm, ...geo } = norm;
  if (radiusEligible(norm) && radiusKm > 0) geo.radiusKm = radiusKm;
  return geo;
}
